import { apiClient } from './api'
import { useSessionStore } from '@/stores/useSessionStore'
import type { Subscription, SubscriptionAction } from '@/types'

export interface OrderItemsParams {
  billing_cycle_type?: number
  latest_cycle?: boolean
  order?: number | string
  subscription_id?: number | string
  status__in?: string
  checkout?: boolean
  is_active?: boolean
}

function unwrap<T>(data: any): T[] {
  if (Array.isArray(data)) {
    return data as T[]
  }
  return (data?.results ?? []) as T[]
}

export const subscriptionService = {
  // GET /order_items/ — the list backing both subscriptions and orders.
  async getOrderItems(params: OrderItemsParams = {}): Promise<Subscription[]> {
    const response = await apiClient.get('/order_items/', { params })
    return unwrap<Subscription>(response.data)
  },

  // GET /order_items/{id}/ — enriched single item (fulfillments, transactions…).
  async getItem(itemId: number | string): Promise<Subscription> {
    const response = await apiClient.get(`/order_items/${itemId}/`)
    return response.data
  },

  // GET /order_items/{id}/fulfillments/
  async getFulfillments(itemId: number | string): Promise<any[]> {
    const response = await apiClient.get(`/order_items/${itemId}/fulfillments/`)
    return unwrap(response.data)
  },

  // GET /fulfillments/{id}/
  async getFulfillment(fulfillmentId: number | string): Promise<any> {
    const response = await apiClient.get(`/fulfillments/${fulfillmentId}/`)
    return response.data
  },

  // GET /order_items/{id}/transactions/
  async getTransactions(itemId: number | string): Promise<any[]> {
    const response = await apiClient.get(`/order_items/${itemId}/transactions/`)
    return unwrap(response.data)
  },

  // GET /campaign_products/popular/ — upsell ("add to your next order").
  async getPopularProducts(): Promise<any[]> {
    const response = await apiClient.get('/campaign_products/popular/')
    return unwrap(response.data)
  },

  // GET /campaign_products/{id}/ — resolve a single product (cross-sell).
  async getCampaignProduct(id: number | string): Promise<any> {
    const response = await apiClient.get(`/campaign_products/${id}/`)
    return response.data
  },

  // Resolve an ordered list of campaign-product IDs to product objects.
  async getCampaignProductsByIds(ids: number[]): Promise<any[]> {
    const results = await Promise.all(
      ids.map(id =>
        apiClient
          .get(`/campaign_products/${id}/`)
          .then(r => r.data)
          .catch(() => null)
      )
    )
    return results.filter(Boolean)
  },

  // GET /order_items/{id}/exchangeables/ — swap candidates.
  async getExchangeables(itemId: number | string, params: Record<string, any> = {}): Promise<any[]> {
    const response = await apiClient.get(`/order_items/${itemId}/exchangeables/`, { params })
    return (response.data?.exchangeables ?? unwrap(response.data)) as any[]
  },

  // POST /sessions/{sessionId}/{action}/ — subscription action middleware.
  async runAction(action: SubscriptionAction, params: Record<string, any> = {}): Promise<any> {
    const sessionId = useSessionStore.getState().session?.id
    if (!sessionId) {
      throw new Error('No active session')
    }
    const response = await apiClient.post(`/sessions/${sessionId}/${action}/`, { params })
    return response.data
  },
}
